import React, { useEffect, useState } from 'react'
import { browserHistory } from 'react-router'
import Page from 'layouts/Page'
import PageWrapper from 'enrollment-platform/PageWrapper'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'

import AdjustPaymentModal from '../../components/ReconciliationV2/AdjustPaymentModal'
import BenefitsTable from '../../components/ReconciliationV2/BenefitsTable'
import ReviewQueue from '../../components/ReconciliationV2/ReviewQueue'
import TransactionDetailsPanel from '../../components/TransactionDetails/TransactionDetailsPanel'
import {
  ReviewQueueKind,
  useReviewQueueCount,
} from '../../api/reconciliation-v2-api-client'
import { replaceOwnedQuery } from '../../components/ReconciliationV2/replaceOwnedQuery'
import {
  kindOfTab,
  QUEUE_TAB,
  RQ_OPEN_KEY,
} from '../../components/ReconciliationV2/ReviewQueue/url-state'
import type { QueueTab } from '../../components/ReconciliationV2/ReviewQueue/url-state'
import { useCanWriteReconciliation } from '../../components/ReconciliationV2/useCanWriteReconciliation'
import { FINANCE_PATH } from '../../constants'
import styles from './ReconciliationV2.module.scss'

type ReconTab = 'benefits' | QueueTab | 'transactions'

const parseTab = (value: unknown): ReconTab => {
  if (value === 'transactions') return value
  if (kindOfTab(value)) return value as QueueTab
  return 'benefits'
}

/**
 * The tab strip, left to right. The two queue tabs carry a count; Benefits and
 * Transaction Details do not.
 *
 * Their labels are the operator-facing names for the two arms of the
 * Required-action queue — "Unallocated payments" for an unposted transaction,
 * "Discrepancies" for a discrepancy (CONTEXT.md). Note the rename stops at the
 * label: the wire param, the components and the `rqDisc*` URL keys all keep the
 * domain spelling, because `kind=discrepancy` is EPS's and renaming our half
 * would desync the two (spec D11). The `tab` slug is the exception that proves
 * it — `tab=flagged` was minted when this tab read "Flagged issues" and is left
 * alone, that name having since moved down to the `manual_review` issue type.
 */
const TABS: readonly {
  tab: ReconTab
  label: string
  kind?: ReviewQueueKind
}[] = [
  { tab: 'benefits', label: 'Benefits' },
  {
    tab: QUEUE_TAB.transaction,
    label: 'Unallocated payments',
    kind: 'transaction',
  },
  { tab: QUEUE_TAB.discrepancy, label: 'Discrepancies', kind: 'discrepancy' },
  { tab: 'transactions', label: 'Transaction Details' },
]

const ReconciliationV2: React.FC = () => {
  const [activeTab, setActiveTabState] = useState<ReconTab>(() => {
    const query = browserHistory.getCurrentLocation().query as
      | Record<string, unknown>
      | undefined
    return parseTab(query?.tab)
  })
  const [addPaymentOpen, setAddPaymentOpen] = useState(false)
  // Write gate — mirrors the EPS AbilityGuard on the manual ee-paid/refund
  // endpoints the "Add Payment" flow posts to.
  const { allowed: canWrite } = useCanWriteReconciliation()

  // The count in each queue tab's label — one query per arm, never summed.
  // There is no grand total any more: "Required action" stopped being a tab when
  // its two arms were promoted to tabs of their own (EMS-10248), so each label
  // now carries only its own arm.
  //
  // useReviewQueueCount pins every count call site to REVIEW_QUEUE_DEFAULTS, so
  // these hash to the SAME React Query keys as whichever panel is open at its
  // defaults, and the pair collapses into one network request per kind. That is
  // what the old limit:1 probe failed to do (COUNT(*) OVER() scans the full arm
  // regardless of limit, so limit:1 was never actually cheaper on the DB).
  // ANYTHING that changes a panel's default params must keep this true.
  //
  // Keeping the queries page-owned means both labels stay UNFILTERED totals:
  // they survive on Benefits and Transaction Details, where <ReviewQueue> is
  // unmounted, and neither moves while the operator filters or paginates the
  // other tab — or its own.
  const queueCounts: Record<ReviewQueueKind, number> = {
    transaction: useReviewQueueCount('transaction'),
    discrepancy: useReviewQueueCount('discrepancy'),
  }

  // Owns `tab`, and drops any still-unconsumed `rqOpen` on the way past.
  //
  // `rqOpen` must NOT survive a manual tab switch: a deep link names one arm,
  // but the value is read against whichever panel is mounted, and the
  // transaction arm takes any string verbatim as a transaction id. A link whose
  // panel never consumed it — a failed first request, or a switch before it
  // settled — would otherwise be picked up by the sibling arm and pin that list
  // to a junk search. This ownership moved up from the ReviewQueue shell when
  // the arms became page tabs; the two must not be separated.
  const setActiveTab = (tab: ReconTab) => {
    setActiveTabState(tab)
    replaceOwnedQuery(['tab', RQ_OPEN_KEY], tab === 'benefits' ? {} : { tab })
  }

  // Keep `activeTab` in sync with `?tab=` when it's changed by anyone other
  // than the tab buttons above — e.g. a child panel's deep link (the Adjust
  // Payment modal's "open in Transaction Details" jump, or a Transaction
  // Details row's "adjust payment" jump) writes `tab=` via replaceOwnedQuery,
  // and this listener flips the rendered tab to match. Without it those deep
  // links would change the URL but leave the wrong panel mounted.
  useEffect(() => {
    const unlisten = browserHistory.listen((location) => {
      const query = (location.query || {}) as Record<string, unknown>
      setActiveTabState(parseTab(query.tab))
    })
    return unlisten
  }, [])

  // No feature-flag gate here: the `admin.finance.reconciliation-v2-enabled`
  // flag only hides the menu tile (see finance/pages/index.tsx). Direct-URL
  // access to this page is intentionally allowed even when the flag is off.
  return (
    <PageWrapper>
      <Page
        title="Payment Reconciliation"
        maxWidth="100%"
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Finance', path: FINANCE_PATH },
              { title: 'Payment Reconciliation' },
            ]}
          />
        }
      >
        <div className={styles.tabRow}>
          <div className={styles.tabList}>
            {TABS.map(({ tab, label, kind }) => (
              <button
                key={tab}
                type="button"
                aria-pressed={activeTab === tab}
                className={`${styles.tabButton}${
                  activeTab === tab ? ` ${styles.active}` : ''
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {label}
                {!!kind && queueCounts[kind] > 0 && (
                  <span>({queueCounts[kind]})</span>
                )}
              </button>
            ))}
          </div>
          {/* Add Payment lives inside the tab row (not the Page header action
          slot) so `.tabRow`'s space-between layout and `.addPaymentButton`'s
          bottom-alignment apply as the scss intends.

          Benefits ONLY. It used to show on everything except the read-only
          Transaction Details ledger, which would have put it on both queue tabs
          once the arms were promoted (EMS-10248). Narrowed deliberately — this
          diverges from the mock, which draws the button on the payments tab;
          confirmed twice with the ticket owner, so treat a review comment about
          the comp as answered rather than as an oversight. */}
          {activeTab === 'benefits' && (
            <button
              type="button"
              className={styles.addPaymentButton}
              onClick={() => setAddPaymentOpen(true)}
              disabled={!canWrite}
            >
              Add Payment
            </button>
          )}
        </div>

        {/* `&&`, so a tab switch UNMOUNTS the panel. Every panel on this route
        round-trips its state through the URL for exactly that reason — see
        ReviewQueue/url-state. */}
        {activeTab === 'benefits' && <BenefitsTable />}
        {activeTab === QUEUE_TAB.transaction && (
          <ReviewQueue kind="transaction" />
        )}
        {activeTab === QUEUE_TAB.discrepancy && (
          <ReviewQueue kind="discrepancy" />
        )}
        {activeTab === 'transactions' && <TransactionDetailsPanel />}

        <AdjustPaymentModal
          open={addPaymentOpen}
          onClose={() => setAddPaymentOpen(false)}
          target={addPaymentOpen ? { kind: 'manual' } : null}
        />
      </Page>
    </PageWrapper>
  )
}

export default ReconciliationV2
